import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FiAlertTriangle } from 'react-icons/fi';
import { logout } from '../redux/actions/authActions';
import { EMAIL_VERIF_API } from '../config/qbase';
import { emailVerifFetch } from '../services/qbaseClient';
import './WithdrawalModal.css';

const WithdrawalModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmStep, setConfirmStep] = useState(false);

    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);

    useEffect(() => {
        if (!isOpen) {
            // 모달이 닫힐 때 상태 초기화
            setFormData({
                email: '',
                password: ''
            });
            setError('');
            setSuccess('');
            setConfirmStep(false);
        } else if (isOpen && user?.email) {
            // 모달이 열릴 때 이메일 자동 설정
            setFormData(prev => ({
                ...prev,
                email: user.email
            }));
        }
    }, [isOpen, user?.email]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // 입력 시 에러 메시지 초기화
        if (error) {
            setError('');
        }
        if (success) {
            setSuccess('');
        }
    };

    const validateForm = () => {
        if (!formData.email.trim()) {
            setError(t('auth:wd.err.emailRequired'));
            return false;
        }

        if (!formData.password.trim()) {
            setError(t('auth:wd.err.passwordRequired'));
            return false;
        }

        if (formData.email !== user?.email) {
            setError(t('auth:wd.err.emailMismatch'));
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!confirmStep) {
            // 첫 번째 단계: 확인 단계로 이동
            if (!validateForm()) {
                return;
            }
            setConfirmStep(true);
            return;
        }

        // 두 번째 단계: 실제 탈퇴 처리
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const requestData = {
                email: formData.email.trim(),
                password: formData.password.trim()
            };

            console.log('🔍 [Withdrawal Debug] 요청 데이터:', { email: requestData.email });

            const response = await emailVerifFetch(EMAIL_VERIF_API.withdraw, requestData);

            const result = await response.json();

            console.log('🔍 [Withdrawal Debug] 서버 응답:', {
                status: response.status,
                ok: response.ok,
                result: result
            });

            if (response.ok && result.success) {
                console.log('✅ 회원 탈퇴 성공:', result);

                // 테넌트 계정(VW_QBASE_USER) 탈회는 **서버가 한다** (EmailVerifService.syncTenantWithdraw).
                // 자격의 일관성은 각 화면이 기억할 일이 아니라 플랫폼이 지킬 불변식이다 —
                // 화면마다 챙기게 두면 세션 없는 경로(임시 비밀번호 발급)에서 실제로 빠졌었다.
                setSuccess(result.message || t('auth:wd.ok'));

                // 3초 후 로그아웃 처리
                setTimeout(() => {
                    dispatch(logout());
                    onClose();
                }, 3000);

            } else {
                console.error('❌ 회원 탈퇴 실패:', result);
                setError(result.message || t('auth:wd.err.failed'));
                setConfirmStep(false); // 첫 번째 단계로 돌아가기
            }
        } catch (error) {
            console.error('회원 탈퇴 오류:', error);
            setError(t('auth:wd.err.unexpected'));
            setConfirmStep(false); // 첫 번째 단계로 돌아가기
        } finally {
            setLoading(false);
        }
    };

    const handleBackToForm = () => {
        setConfirmStep(false);
        setError('');
    };

    if (!isOpen) return null;

    return (
        <div className="withdrawal-modal-overlay">
            <div className="withdrawal-modal">
                <div className="withdrawal-modal-header">
                    <h2 className="withdrawal-modal-title">
                        {confirmStep ? t('auth:wd.confirmTitle') : t('auth:wd.title')}
                    </h2>
                    {!loading && !success && (
                        <button
                            type="button"
                            className="withdrawal-modal-close"
                            onClick={onClose}
                            aria-label={t('auth:wd.close')}
                        >
                            ×
                        </button>
                    )}
                </div>

                {!confirmStep ? (
                    <form className="withdrawal-modal-form" onSubmit={handleSubmit}>
                        <div className="withdrawal-warning">
                            <div className="warning-icon">⚠️</div>
                            <div className="warning-text">
                                <strong>{t('auth:wd.cautionLabel')}</strong> {t('auth:wd.cautionBody')}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email" className="form-label">
                                {t('auth:wd.email')}
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder={t('auth:wd.emailPlaceholder')}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">
                                {t('auth:wd.password')}
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder={t('auth:wd.passwordPlaceholder')}
                                disabled={loading}
                                required
                            />
                        </div>

                        {error && (
                            <div className="error-message">
                                <div className="error-icon"><FiAlertTriangle size={14} /></div>
                                <div className="error-text">{error}</div>
                            </div>
                        )}

                        <div className="withdrawal-modal-buttons">
                            <button
                                type="submit"
                                className="withdrawal-button danger"
                                disabled={loading || !formData.email.trim() || !formData.password.trim()}
                            >
                                {t('auth:wd.next')}
                            </button>

                            <button
                                type="button"
                                className="withdrawal-button secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                {t('auth:wd.cancel')}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="withdrawal-confirm">
                        <div className="confirm-warning">
                            <div className="warning-icon">🚨</div>
                            <div className="warning-text">
                                <strong>{t('auth:wd.reallyProceed')}</strong>
                                <br />
                                {t('auth:wd.irreversible')}
                            </div>
                        </div>

                        <div className="confirm-info">
                            <p><strong>{t('auth:wd.accountLabel')}</strong> {formData.email}</p>
                            <p><strong>{t('auth:wd.whatIsDeleted')}</strong></p>
                            <ul>
                                <li>{t('auth:wd.item.profile')}</li>
                                <li>{t('auth:wd.item.sqlFiles')}</li>
                                <li>{t('auth:wd.item.challenge')}</li>
                                <li>{t('auth:wd.item.other')}</li>
                            </ul>
                        </div>

                        {error && (
                            <div className="error-message">
                                <div className="error-icon"><FiAlertTriangle size={14} /></div>
                                <div className="error-text">{error}</div>
                            </div>
                        )}

                        {success && (
                            <div className="success-message">
                                <div className="success-icon">✅</div>
                                <div className="success-text">{success}</div>
                            </div>
                        )}

                        {!success && (
                            <div className="withdrawal-modal-buttons">
                                <button
                                    type="button"
                                    className="withdrawal-button danger"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? t('auth:wd.processing') : t('auth:wd.execute')}
                                </button>

                                <button
                                    type="button"
                                    className="withdrawal-button secondary"
                                    onClick={handleBackToForm}
                                    disabled={loading}
                                >
                                    {t('auth:wd.back')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WithdrawalModal;